import type {
  ApiResponse,
  BookingListQuery,
  CreateBookingRequest,
  SafeBooking,
} from "@playarena/shared";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getEnv } from "@/utils/helpers";

export const bookingApi = createApi({
  reducerPath: "bookingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getEnv()?.VITE_API_URL}/${getEnv()?.VITE_APP_VERSION}/booking`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Booking"],
  endpoints: builder => ({
    getMyBookings: builder.query<ApiResponse<SafeBooking[]>, BookingListQuery>({
      query: params => ({
        url: "/list/my",
        params,
      }),
      providesTags: ["Booking"],
    }),

    getBookingById: builder.query<ApiResponse<SafeBooking>, string>({
      query: id => `/${id}`,
      providesTags: (result, error, id) => [{ type: "Booking", id }],
    }),

    createBooking: builder.mutation<ApiResponse<SafeBooking>, CreateBookingRequest>({
      query: data => ({
        url: "/create",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Clear selected seats after successful booking
            dispatch({ type: "booking/clearSelectedSeats" });
            // Set current booking
            dispatch({ type: "booking/setCurrentBooking", payload: data.data });
          }
        } catch (error) {
          console.error("bookingApi.createBooking error:", error);
        }
      },
      invalidatesTags: ["Booking"],
    }),

    cancelBooking: builder.mutation<ApiResponse<SafeBooking>, string>({
      query: id => ({
        url: `/${id}/cancel`,
        method: "PATCH",
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update current booking if it's the one being cancelled
            dispatch({ type: "booking/setCurrentBooking", payload: data.data });
          }
        } catch (error) {
          console.error("bookingApi.cancelBooking error:", error);
        }
      },
      invalidatesTags: (result, error, id) => [{ type: "Booking", id }, "Booking"],
    }),

    // "organizer", "manager", "superadmin"
    getBookingsByEvent: builder.query<ApiResponse<SafeBooking[]>, string>({
      query: eventId => `/list/event/${eventId}`,
      providesTags: ["Booking"],
    }),

    // "organizer", "manager", "superadmin"
    getBookingsByGround: builder.query<ApiResponse<SafeBooking[]>, string>({
      query: groundId => `/list/ground/${groundId}`,
      providesTags: ["Booking"],
    }),

    updateBooking: builder.mutation<
      ApiResponse<SafeBooking>,
      { id: string; data: Partial<CreateBookingRequest> }
    >({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update current booking if it's the one being updated
            dispatch({ type: "booking/setCurrentBooking", payload: data.data });
          }
        } catch (error) {
          console.error("bookingApi.updateBooking error:", error);
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: "Booking", id }, "Booking"],
    }),

    confirmPayment: builder.mutation<
      ApiResponse<SafeBooking>,
      { bookingId: string; paymentIntentId: string }
    >({
      query: data => ({
        url: "/confirm-payment",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update current booking with payment confirmation
            dispatch({ type: "booking/setCurrentBooking", payload: data.data });
          }
        } catch (error) {
          console.error("bookingApi.confirmPayment error:", error);
        }
      },
      invalidatesTags: ["Booking"],
    }),
  }),
});

export const {
  useGetMyBookingsQuery,
  useGetBookingByIdQuery,
  useCreateBookingMutation,
  useCancelBookingMutation,
  useGetBookingsByEventQuery,
  useGetBookingsByGroundQuery,
  useUpdateBookingMutation,
  useConfirmPaymentMutation,
} = bookingApi;
